import { useCallback, useEffect, useRef, useState } from "react";
import { FaBell, FaGear, FaRegEnvelope, FaUser } from "react-icons/fa6";
import { SlLogout } from "react-icons/sl";
import { NavLink, useLocation } from "react-router-dom";
import { useOnClickOutside, useScript } from "usehooks-ts";

import Notifications from "@hd/notifications/Notifications";
import { useNotifications } from "@hd/notifications/useNotifications";
import { ListItemDto } from "@hd/types/dto/shared/list-item.dto";

import logo_small from "../../../assets/logo_small.svg";
import menu_toggle from "../../../assets/sidebar_toggler_icon_grey.png";
import { useSidebar } from "../sidebar/sidebar.store";
import Notices from "./components/Notices";
import HeaderDropdownMenu, { DropdownItem } from "./HeaderDropdownMenu";

interface HeaderProps {
  logout: () => void;
  changeGroup: (id: number) => void;
  editUsersAndPermissions: boolean;
  userId: string;
  userFullName: string;
  activeGroups: ListItemDto[];
  // activeCompanies: ListItemDto[];
  activeGroupName: string;
}

function Header({
  userId,
  userFullName,
  logout,
  changeGroup,
  editUsersAndPermissions,
  activeGroups,
  activeGroupName,
}: // activeCompanies,
HeaderProps) {
  const [showSettings, setShowSettings] = useState(false);
  const [settingsItems, setSettingsItems] = useState<DropdownItem[]>([]);

  const [showAccounts, setShowAccounts] = useState(false);
  const [accountsItems, setAccountsItems] = useState<DropdownItem[]>([]);

  const init = useCallback(() => {
    setSettingsItems([
      { text: "Impostazioni", url: "/Settings" },
      { text: "Gestione Account Email", url: "/MailAccountGroup" },
      {
        text: "Acquista crediti",
        url: "/ExtraMailPurchased/Create",
      },
      { text: "Informazioni legali", url: "/LegalNotice" },
    ]);

    const _items: DropdownItem[] = [];
    if (editUsersAndPermissions) {
      _items.push({ text: "Utenti e Permessi", url: "/Users" });
    }
    _items.push(
      ...[
        {
          text: "Modifica account",
          url: "/Users/Edit/" + userId,
        },
        {
          text: "Pagina di riepilogo",
          url: "/",
        },
        {
          text: "Disconnettimi",
          callback: () => {
            logout();
          },
        },
      ]
    );
    if (activeGroups.length > 0) {
      const item: DropdownItem = {
        text: "Usa hoteldoor come",
        children: [],
        showSearch: true,
      };
      activeGroups.forEach((group) => {
        item.children?.push({
          text: group.name,
          // url: "/Home/ChangeGroup/" + group.id,
          callback: () => {
            changeGroup(group.id);
          },
        });
      });
      _items.push(item);
    }

    setAccountsItems(_items);
  }, [userId, logout, changeGroup, editUsersAndPermissions, activeGroups]);

  useEffect(() => {
    init();
  }, [init]);

  useScript(`/beamerprovider.js`, {
    removeOnUnmount: true,
    id: "beamer-init",
  });

  useScript(`https://app.getbeamer.com/js/beamer-embed.js`, {
    removeOnUnmount: true,
    id: "beamer",
  });

  const {
    unreadMessages,
    isOpen: isOpenNotifications,
    toggleOpen: toggleOpenNotifications,
    setOpen: setOpenNotifications,
  } = useNotifications();

  const notificationsElementRef = useRef<HTMLDivElement>(null);
  const onClickOutsideNotifications = useCallback(
    (e: MouseEvent | TouchEvent | FocusEvent) => {
      if (
        isOpenNotifications &&
        !(e.target as HTMLElement).classList.contains("company-option")
      ) {
        setOpenNotifications(false);
      }
    },
    [isOpenNotifications, setOpenNotifications]
  );
  useOnClickOutside(notificationsElementRef, onClickOutsideNotifications);

  const location = useLocation();

  const { toggleOpen: toggleOpenSidebar, isOpen: openSidebar } = useSidebar();

  return (
    <div
      className="pr-4 flex items-center z-10 text-secondary sticky top-0 bg-white"
      style={{ boxShadow: "0 1px 10px 0 rgba(50,50,50,.2)" }}
    >
      <div
        className={
          "ml-1 w-10 flex flex-col items-center md:w-48 md:flex-row md:ml-4 flex-shrink-0 " +
          (openSidebar ? "" : "!w-10 !ml-1 !flex-col")
        }
      >
        <NavLink
          to="/"
          onClick={(e) => {
            if (location.pathname === "/") {
              e.preventDefault();
            }
          }}
        >
          <img
            className={
              "md:ml-12 w-6 md:w-8 " + (openSidebar ? "" : " !ml-0 !w-6")
            }
            src={logo_small}
          ></img>
        </NavLink>
        <img
          className={
            "md:ml-16 mt-1 md:mt-0 opacity-60 hover:opacity-100 cursor-pointer " +
            (openSidebar ? "" : " !ml-0 !mt-1")
          }
          src={menu_toggle}
          onClick={() => toggleOpenSidebar()}
        />
      </div>
      <div className="text-sm font-thin ml-5">
        <span className="hidden md:inline mr-2">
          Stai utilizzando hoteldoor come:
        </span>
        <span className="font-bold">{userFullName} </span>
          per
          <span className="font-bold"> {activeGroupName}</span>
      </div>
      <div className="ml-auto flex items-center">
        <div
          className="flex items-center relative group"
          ref={notificationsElementRef}
        >
          <div
            className={
              "p-5 h-full cursor-pointer hover:bg-slate-50 " +
              (isOpenNotifications ? "bg-slate-50" : "")
            }
            onClick={(e) => {
              e.stopPropagation();
              toggleOpenNotifications();
            }}
          >
            {/* <i className="fa fa-envelope-o fa-lg relative"> */}
            <span className="relative">
              <FaRegEnvelope className="text-lg"></FaRegEnvelope>
              {unreadMessages > 0 && (
                <div className="font-sans absolute inline-flex items-center justify-center px-1 min-w-4 h-4 text-xxs font-semibold text-white bg-accent rounded-full -top-3 -end-3">
                  {unreadMessages}
                </div>
              )}
            </span>
          </div>

          <div
            className={`absolute top-full mt-1 right-0 ${
              isOpenNotifications ? "block" : "hidden"
            }`}
          >
            <Notifications />
          </div>
        </div>

        <div
          className="flex items-center relative group"
          // onMouseEnter={() => setShowSettings(true)}
          // onMouseLeave={() => setShowSettings(false)}
        >
          <div
            className="py-5 px-3 md:p-5 h-full flex items-center cursor-pointer hover:bg-slate-50"
            onClick={() => setShowAccounts(true)}
          >
            <FaUser className="text-lg"></FaUser>
          </div>

          <HeaderDropdownMenu
            items={accountsItems}
            open={showAccounts}
            setOpen={(value) => setShowAccounts(value)}
          ></HeaderDropdownMenu>
        </div>
        <div
          className="flex items-center relative group"
          // onMouseEnter={() => setShowSettings(true)}
          // onMouseLeave={() => setShowSettings(false)}
        >
          <div
            className="py-5 px-3 md:p-5 h-full flex items-center cursor-pointer hover:bg-slate-50"
            onClick={() => setShowSettings(true)}
          >
            <FaGear className="text-lg"></FaGear>
          </div>
          <HeaderDropdownMenu
            items={settingsItems}
            open={showSettings}
            setOpen={setShowSettings}
          ></HeaderDropdownMenu>
        </div>
        {/* <NavLink
          to="/Calendar"
          className="py-5 px-3 md:p-5 h-full flex items-center cursor-pointer hover:bg-slate-50"
        >
          <i className="fa fa-calendar fa-lg  "></i>
        </NavLink> */}

        <Notices></Notices>

        <div className="py-5 px-3 md:p-5 h-full flex items-center cursor-pointer hover:bg-slate-50 App__Beamer">
          <div className="relative">
            <FaBell className="text-lg"></FaBell>
            <span
              style={{ display: "none" }}
              className="badge absolute items-center justify-center px-1 min-w-4 h-4 text-xxs font-semibold text-white bg-accent rounded-full -top-3 -end-3"
            ></span>
          </div>
        </div>
        <div
          className="py-5 px-3 md:p-5 h-full flex items-center cursor-pointer hover:bg-slate-50"
          onClick={logout}
        >
          <SlLogout className="text-lg" />
        </div>
      </div>
    </div>
  );
}

export default Header;
